import dayjs from "dayjs";
import { useMemo } from "react";

import { LineChart } from "@design-system/LineChart/LineChart";

import { useResourceGroupEmployeeUsageLineChartQuery } from "./ResourceGroupEmployeeUsageLineChart.g";

type Props = {
  clientApplicationId: string;
  daysToShow?: number;
};

export const ResourceGroupEmployeeUsageLineChart = ({
  clientApplicationId,
  daysToShow = 30,
}: Props) => {
  const { dateFrom, dateTo } = useMemo(() => {
    const to = dayjs();
    const from = to.subtract(daysToShow, "day");
    return {
      dateFrom: from.format("YYYY-MM-DD"),
      dateTo: to.format("YYYY-MM-DD"),
    };
  }, [daysToShow]);

  const { data } = useResourceGroupEmployeeUsageLineChartQuery({
    clientApplicationId,
    dateFrom,
    dateTo,
  });

  const chartData = useMemo(() => {
    if (!data?.resourceGroupUsage) {
      return { chartData: [], weekendDates: [] };
    }

    // Create a map of date to employee count
    const usageByDate = new Map<string, number>();
    data.resourceGroupUsage.forEach((usage) => {
      if (usage?.date && usage.employeeCount !== undefined) {
        usageByDate.set(usage.date, usage.employeeCount);
      }
    });

    // Generate data for all dates in our range
    const endDate = dayjs(dateTo);
    const chartData = Array.from({ length: daysToShow }, (_, i) => {
      const currentDate = endDate.subtract(daysToShow - 1 - i, "day");
      const dateStr = currentDate.format("YYYY-MM-DD");
      const displayDate = currentDate.format("DD/MM/YYYY");
      const employeeCount = usageByDate.get(dateStr) || 0;

      return {
        x: displayDate,
        y: employeeCount,
      };
    });

    // Separately calculate weekend dates for highlighting
    const weekendDates = Array.from({ length: daysToShow }, (_, i) => {
      const currentDate = endDate.subtract(daysToShow - 1 - i, "day");
      const isWeekend = currentDate.day() === 0 || currentDate.day() === 6; // Sunday = 0, Saturday = 6
      return isWeekend ? currentDate.format("DD/MM/YYYY") : null;
    }).filter(Boolean) as string[];

    return { chartData, weekendDates };
  }, [data, dateTo, daysToShow]);

  return (
    <LineChart
      data={[
        {
          id: "user-usage",
          color: "red",
          data: chartData.chartData,
        },
      ]}
      weekendDates={chartData.weekendDates}
      xAxisName=""
      yAxisName="User count"
      tooltipLabel="Users"
    />
  );
};
